import { useEffect, useMemo, useCallback, useState } from 'react';
import { Badge, Button, Select, Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { getQuotes } from '@/pages/Quotes/services';
import { useGlobalContext } from '@/context/GlobalContext';
import cn from 'classnames';
import { Link, useLocation } from 'react-router-dom';
import { routes } from '@/config/routes';
import { ROUTE_ID } from '@/config/constants';
import { ShoppingCartOutlined } from '@ant-design/icons';
import { useAuth } from '@/context/AuthContext';
import styles from './styles.module.scss';
import FloatLabel from '@/components/common/FloatLabel';
import { getQuoteFromLocalStorage } from '@/utils/quotes';
import { deviceQueries } from '@/config/theme';
import { useMediaQuery } from 'react-responsive';

const Quote = () => {
    const { selectedQuote, setQuote, quoteDetails } = useGlobalContext();
    const { auth, user } = useAuth();
    const [search, setSearch] = useState('');
    const { pathname } = useLocation();

    const isCommandCenter = useMemo(() => {
        return pathname.includes(routes.comandCenter.products.root);
    }, [pathname]);

    const partnerAccountId = useMemo(() => {
        return isCommandCenter ? undefined : user?.accountId;
    }, [isCommandCenter, user]);

    const { data, isLoading } = useQuery({
        queryKey: ['quotes', search, partnerAccountId],
        queryFn: () => getQuotes({ name: search, partnerAccountId, limit: 20 }),
        enabled: auth?.isAuthenticated,
    });

    const availableQuotes = useMemo(() => {
        return data?.quotes.filter((quote) => !quote.hasOrderPlaced) || [];
    }, [data]);

    const cachedQuote = useMemo(() => {
        if (!user?.accountId) return undefined;
        return getQuoteFromLocalStorage(user.accountId, isCommandCenter);
    }, [user, isCommandCenter]);

    useEffect(() => {
        if (!cachedQuote && !quoteDetails && availableQuotes.length) {
            setQuote(availableQuotes[0]);
        }
        let fetchedQuote;
        if (quoteDetails && availableQuotes.length) {
            fetchedQuote = !quoteDetails.hasOrderPlaced ? quoteDetails : availableQuotes[0];
        }
        if (fetchedQuote) {
            setQuote(fetchedQuote);
        }
    }, [availableQuotes, setQuote, quoteDetails, cachedQuote]);

    const onSearch = useCallback((value: string) => {
        setSearch(value);
    }, []);

    const onSelect = useCallback((value: string) => {
        const quote = availableQuotes.find((quote) => quote.id === value);
        if (quote) {
            setQuote(quote);
        }
    }, [setQuote, availableQuotes]);

    const isSmallDevice = useMediaQuery({ query: deviceQueries.mdDevices });

    if (!availableQuotes.length) return null;

    return (
        <div className={cn(styles.quoteBox)}>
            <FloatLabel className={styles.quoteSelect} label="Adding Items to Quote" value={selectedQuote?.name}>
                <Select
                    style={{ width: '100%' }}
                    value={selectedQuote?.id}
                    showSearch
                    onSearch={onSearch}
                    filterOption={false}
                    onSelect={onSelect}
                    loading={isLoading}
                    optionLabelProp='label'
                >
                    {
                        availableQuotes?.map((quote) => {
                            return (
                                <Select.Option key={quote.id} value={quote.id} label={quote.name}>
                                    {quote.name}
                                    {isCommandCenter && quote?.partnerAccount?.accountName && (
                                        <div>
                                            <Typography.Text type="secondary">{quote.partnerAccount.accountName}</Typography.Text>
                                        </div>
                                    )}
                                </Select.Option>
                            )
                        })
                    }
                </Select>
            </FloatLabel>
            {
                selectedQuote && (
                    <Link to={isCommandCenter ? routes.comandCenter.quotes.details.replace(ROUTE_ID, selectedQuote.id) : routes.quotes.details.replace(ROUTE_ID, selectedQuote.id)}>
                        <Badge count={selectedQuote?.items?.length}>
                            <Button
                                type="primary"
                                icon={isSmallDevice ? undefined : <ShoppingCartOutlined />}
                            >
                                Go To Cart
                            </Button>
                        </Badge>
                    </Link>
                )
            }
        </div>
    )
};

export default Quote;
